import { useState } from 'react';

import { useGoogleLogin as useGoogleOAuthLogin } from '@react-oauth/google';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { loginWithGoogle, refreshTokens, registerWithGoogle } from '../api/authApi';
import { useAuthStore } from '../store/authStore';
import type { AccountInfo, AuthTokens, GoogleUser, User } from '../types/auth.types';
import type { ApiError } from '@/types/api.types';

const LOGIN_ERROR_MAP: Partial<Record<string, string>> = {
  USER_NOT_FOUND: 'errors.userNotFound',
  UNAUTHORIZED: 'errors.googleInvalidToken',
  INTERNAL_SERVER_ERROR: 'errors.serverError',
};

const REGISTER_ERROR_MAP: Partial<Record<string, string>> = {
  VALIDATION_ERROR: 'errors.googleValidationError',
  UNAUTHORIZED: 'errors.googleInvalidToken',
  ACCOUNT_ALREADY_EXISTS: 'errors.accountAlreadyExists',
  INTERNAL_SERVER_ERROR: 'errors.serverError',
};

function mapGoogleUser(googleUser: GoogleUser): User {
  return {
    id: googleUser.cognitoSub,
    email: googleUser.email,
    firstName: googleUser.firstName,
    lastName: googleUser.lastName,
    role: 'user',
    avatarUrl: googleUser.profilePicture,
    createdAt: new Date().toISOString(),
  };
}

interface UseGoogleAuthOptions {
  mode: 'login' | 'register';
  onRegisterSuccess?: (user: User, accountId: string) => void;
  onLoginSuccess?: (accounts: AccountInfo[]) => void;
}

export function useGoogleAuth({ mode, onRegisterSuccess, onLoginSuccess }: UseGoogleAuthOptions) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setUser, setTokens, setActiveAccount } = useAuthStore();
  const [googleError, setGoogleError] = useState<string | null>(null);

  function applyAuthResponse(googleUser: GoogleUser, idToken: string, accessToken: string, refreshToken: string): User {
    const user = mapGoogleUser(googleUser);
    const tokens: AuthTokens = { idToken, accessToken, refreshToken, expiresIn: 3600 };
    setUser(user);
    setTokens(tokens);
    return user;
  }

  const loginMutation = useMutation({
    mutationFn: (accessToken: string) => loginWithGoogle(accessToken),
    onSuccess: (response) => {
      const { tokens, user, accounts } = response.data;
      applyAuthResponse(user, tokens.idToken, tokens.accessToken, tokens.refreshToken);
      if (onLoginSuccess) {
        onLoginSuccess(accounts ?? []);
      } else {
        if (accounts?.length) setActiveAccount(accounts[0]);
        navigate('/account', { replace: true });
      }
    },
    onError: (error: unknown) => {
      const apiError = error as ApiError;
      const i18nKey = LOGIN_ERROR_MAP[apiError.code] ?? 'auth.googleFailed';
      setGoogleError(t(i18nKey));
    },
  });

  const registerMutation = useMutation({
    mutationFn: (accessToken: string) => registerWithGoogle(accessToken),
    onSuccess: async (response) => {
      const { tokens, user, account } = response.data;
      const mappedUser = applyAuthResponse(user, tokens.idToken, tokens.accessToken, tokens.refreshToken);

      // The registration token is minted before the backend assigns account-level
      // roles in Cognito. Refreshing forces a new idToken that carries those claims,
      // which the API Gateway JWT authorizer on /v1/settings/** requires.
      try {
        const refreshed = await refreshTokens(tokens.refreshToken);
        setTokens(refreshed.data);
      } catch {
        // Proceed with the initial token if refresh fails; Step 2 will surface the
        // 403 and the user can retry.
      }

      // Persist the accountId immediately so every protected API (e.g. /specialties)
      // can read it via selectActiveAccount without waiting for onboarding to finish.
      setActiveAccount({ accountId: account.id, status: account.status, businessName: '', roles: [] });

      onRegisterSuccess?.(mappedUser, account.id);
    },
    onError: (error: unknown) => {
      const apiError = error as ApiError;
      if (apiError.code === 'ACCOUNT_ALREADY_EXISTS') {
        navigate('/login');
        return;
      }
      const i18nKey = REGISTER_ERROR_MAP[apiError.code] ?? 'auth.googleFailed';
      setGoogleError(t(i18nKey));
    },
  });

  const triggerGoogleAuth = useGoogleOAuthLogin({
    onSuccess: (tokenResponse) => {
      setGoogleError(null);
      if (mode === 'login') {
        loginMutation.mutate(tokenResponse.access_token);
      } else {
        registerMutation.mutate(tokenResponse.access_token);
      }
    },
    onError: () => setGoogleError(t('auth.googleFailed')),
  });

  return {
    triggerGoogleAuth,
    isPending: loginMutation.isPending || registerMutation.isPending,
    error: googleError,
  };
}
